import React, { lazy, useEffect, useState, Suspense, Fragment } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import styled from '@mui/styles/styled';
import makeStyles from '@mui/styles/makeStyles';
import { List, ListItem, Menu, MenuItem, AppBar, Toolbar, IconButton, Hidden, SwipeableDrawer, Drawer, LinearProgress, Divider, ListSubheader, useMediaQuery, CircularProgress } from '@mui/material';
import { useStateStore } from '../../services/State';
import { userLogout } from '../../services/coa-authorization';

import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import SystemUpdateAlt from '@mui/icons-material/SystemUpdateAlt';

import { usePromiseTracker } from 'react-promise-tracker';
import * as serviceWorkerRegistration from '../../serviceWorkerRegistration';
import { useStorageState } from 'react-storage-hooks';
import packageJson from '../../../package.json';
import usePushNotification from '../PushNotificationSubscriber';

/** Lazy Loaded Sub-Menus */
const PressReleaseSubMenu = lazy(() => import('./SideMenu/PressReleaseLists'));
const PodcastSubMenu = lazy(() => import ('./SideMenu/PodcastList'));
const PublicationSubMenu = lazy(() => import ('./SideMenu/PublicationList'));

//Layout styles
const drawerWidth = 240;
const useStyles = makeStyles(theme => ({
  grow: {
    flexGrow: 1,
  },
  navIconHideSm: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navIconHideLg: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  menuButton: {
    marginLeft: -12,
    marginRight: 20,
  },
  appBar: {
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 1,
  },
  drawerPaper: {
    position: 'relative',
    width: drawerWidth,
    transitionDuration: '0ms !important',
  },
  drawerDocked: {
    height: '100%',
  },
  toolbar: theme.mixins.toolbar,
  list: {
  },
  content: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    minWidth: 0, // So the Typography noWrap works
    height: '100%',
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  contentShift: {
    [theme.breakpoints.up('md')]: {
      marginLeft: -drawerWidth,
    },
  },
}));

//New Style method
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

function AppSkeleton({ pages, children }) {
  const location = useLocation();
  const classes = useStyles();
  const hiddenMdUp = useMediaQuery(theme => theme.breakpoints.up('md'));
  /* Mobile Phone drawer controller (swipe) */
  const [drawer, setDrawer] = useState(false);
  /* Desktop drawer, toggle preference */
  const [persistDrawer, setPersistDrawer] = useStorageState(localStorage, 'persist-drawer', false);
  const [currentPath, setCurrentPath] = useState(location.pathname.replace(/\/$/, '') || '/'); //we use currentPath === page.path in a number of places, this breaks when a trailing slash is present
  const [accountIcon, setAccountIcon] = useState(null);
  const menuList = pages || [];
  const [user] = useStateStore('userProfile');// userAuthStatus("pressrelease");
  const { promiseInProgress } = usePromiseTracker();
  const [updatedSWVersion, setUupdatedSWVersion] = useState(false);
  /* Push Notifications */
  //let [pushNotifications, togglePushNotifications] = (() => import("../PushNotificationSubscriber").then((usePushNotification) => usePushNotification))();

  /** Service Worker - Check for new version stuff */
  serviceWorkerRegistration.register({
    onUpdate: setUupdatedSWVersion
  });
  const handleSWRegistrationUpdate = () => {
    let refreshing;
    navigator.serviceWorker.addEventListener('controllerchange', function () {
      if (refreshing) return;
      window.location.reload();
      refreshing = true;
    });
    updatedSWVersion.waiting.postMessage({type: 'SKIP_WAITING'})
  }
  
  useEffect(() => {
    //can't get current state, only initial state
    document.body.addEventListener('keyup', (event) => {
      if (event.code === "Escape") {
        setPersistDrawer(!window.globalPersistDrawer)
      }
    });
  }, [setPersistDrawer]);
  
  useEffect(() => {
    window.globalPersistDrawer = persistDrawer;
  }, [persistDrawer]);

  /** Checks page location with menu path for functional match (ignoring trailing slash and dynamic ids) */
  //const isPathSelected = (current, compare) => current.replace(/[0-9]/g, '').replace(/\/$/, '') === compare.replace(/[0-9]/g, '').replace(/\/$/, '');

  /** Side Navigation */
  const drawerContents = (
    <React.Fragment>
      <List component="nav">
        {menuList.map((item, i) => (
          <ListItem key={i} button component={Link} to={item.path}
            selected={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}>{item.name}</ListItem>
        ))}
      </List>
      <Routes>
        <Route path="/press-release-editor/*" element={
          <Suspense fallback={<CircularProgress />}><PressReleaseSubMenu/></Suspense>
        }></Route>
        <Route path="/podcast-editor/*" element={
          <Suspense fallback={<CircularProgress />}><PodcastSubMenu location={location} /></Suspense>
        }></Route>
        <Route path="/publication-editor/*" element={
          <Suspense fallback={<CircularProgress />}><PublicationSubMenu location={location} /></Suspense>
        }></Route>
      </Routes>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <AppBar position="absolute" className={classes.appBar}>
        <Toolbar>
          <IconButton onClick={() => { setDrawer(!drawer)}} className={`${classes.menuButton} ${classes.navIconHideLg}`} color="inherit" aria-label="Menu">
            <MenuIcon />
          </IconButton>
          <IconButton onClick={() => { setPersistDrawer(!persistDrawer)}} className={`${classes.menuButton} ${classes.navIconHideSm}`} color="inherit" aria-label="Menu">
            <MenuIcon />
          </IconButton>
          <h1 className={classes.grow}>My News &mdash; {`${menuList.find(e => e.path === location.pathname || location.pathname.startsWith(`${e.path}/`))?.name || ''}`}</h1>
          <div>
            {updatedSWVersion && (
              <IconButton title="New Version Available" onClick={handleSWRegistrationUpdate} style={{color: '#FFF'}}>
                <SystemUpdateAlt></SystemUpdateAlt>
              </IconButton>
            )}
            <IconButton
              aria-owns={accountIcon ? 'menu-appbar' : null}
              aria-haspopup="true"
              onClick={(event) => { setAccountIcon(event.currentTarget)}}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={accountIcon}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(accountIcon)}
              onClose={() => { setAccountIcon(null)}}
            >
              <MenuItem onClick={() => { setAccountIcon(null)}}>{user.email}</MenuItem>
              {/*pushNotifications && (<MenuItem title="Experimental" onClick={() => { togglePushNotifications(); setAccountIcon(null) }}>Toggle Push Notifications: {(pushNotifications ? 'Off' : 'On')}</MenuItem>)*/}
              <MenuItem onClick={userLogout}>Logout &nbsp;<small>(v{packageJson.version})</small></MenuItem>
            </Menu>
          </div>
        </Toolbar>
        {promiseInProgress && (<LinearProgress />)}
      </AppBar>
      {hiddenMdUp ? null : 
        <SwipeableDrawer
          open={drawer}
          onClose={() => { setDrawer(!drawer)}}
          onOpen={() => { setDrawer(!drawer)}}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <DrawerHeader />
          <div className={classes.list}>
            {drawerContents}
          </div>
        </SwipeableDrawer>
      }
      <Drawer sx={{display: { md: 'block', xs: 'none'}}}
        variant="persistent"
        anchor="left"
        open={persistDrawer}
        classes={{
          paper: classes.drawerPaper,
          docked: classes.drawerDocked,
        }}
      >
        <div className={classes.list}>
          <div className={classes.toolbar} />
          {drawerContents}
        </div>
      </Drawer>
      <main className={`${classes.content} ${(!persistDrawer && classes.contentShift)}`}>
        <div className={classes.toolbar} />
        {children}
      </main>
    </React.Fragment>
  );
}

export default AppSkeleton;
